import React, { useState } from 'react';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  GripVertical,
  Plus,
  Zap,
  Globe,
  Target,
  GitBranch,
  Filter,
  Building2,
  Compass,
  UserPlus,
  MapPin,
  Mail,
  MessageSquare,
  CheckSquare,
  Clock,
  CheckCircle2,
  Layers,
  X,
  Sparkles,
  PieChart,
  Shuffle,
} from 'lucide-react';
import { nodeCategories } from './flowUtils';
import { useWorkflowStore } from '../../hooks/useWorkflowStore';

const iconMap = {
  Zap,
  Globe,
  Target,
  GitBranch,
  Filter,
  Building2,
  Compass,
  UserPlus,
  MapPin,
  Mail,
  MessageSquare,
  CheckSquare,
  Clock,
  CheckCircle2,
  Sparkles,
  PieChart,
  Shuffle,
};

export default function BlockPaletteSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [search, setSearch] = useState('');
  const addNode = useWorkflowStore((state) => state.addNode);

  const onDragStart = (event, nodeTemplate) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(nodeTemplate));
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleQuickAdd = (template) => {
    addNode({
      type: template.type,
      data: {
        label: template.title || template.type,
        subtitle: template.subtitle,
        icon: template.icon,
        ...template,
      },
    });
  };

  if (collapsed) {
    return (
      <div
        style={{
          width: 46,
          background: 'var(--bg-surface)',
          borderRight: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: 14,
          zIndex: 10,
        }}
      >
        <button
          className="btn btn-ghost btn-icon"
          style={{ width: 32, height: 32, padding: 0 }}
          onClick={() => setCollapsed(false)}
          title="Expand Block Library"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    );
  }

  return (
    <aside
      style={{
        width: 300,
        background: 'var(--bg-surface)',
        borderRight: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        zIndex: 10,
        userSelect: 'none',
      }}
    >
      {/* Drawer Header */}
      <div
        style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0,0,0,0.02)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: 'var(--accent-soft)',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Layers size={16} />
          </div>
          <div>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, lineHeight: 1.2 }}>
              Block Library
            </h3>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)' }}>
              Drag onto canvas or click +
            </span>
          </div>
        </div>

        <button
          className="btn btn-ghost btn-icon"
          style={{ width: 28, height: 28, padding: 0 }}
          onClick={() => setCollapsed(true)}
          title="Collapse Panel"
        >
          <ChevronLeft size={16} />
        </button>
      </div>

      {/* Search Bar */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ position: 'relative' }}>
          <Search
            size={14}
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
            }}
          />
          <input
            type="text"
            className="input"
            placeholder="Search blocks (e.g. Route, Score)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              paddingLeft: 34,
              paddingRight: search ? 30 : 12,
              height: 34,
              fontSize: 12,
              background: 'var(--bg-raised)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              width: '100%',
              outline: 'none',
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Block Categories & Cards */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {nodeCategories.map((cat) => {
          const filteredItems = cat.items.filter(
            (item) =>
              item.title.toLowerCase().includes(search.toLowerCase()) ||
              item.subtitle.toLowerCase().includes(search.toLowerCase())
          );

          if (filteredItems.length === 0) return null;

          return (
            <div key={cat.id}>
              {/* Category Header */}
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: cat.color,
                  marginBottom: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <div
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: cat.color,
                    boxShadow: `0 0 6px ${cat.color}`,
                  }}
                />
                {cat.name}
              </div>

              {/* Items List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {filteredItems.map((item, idx) => {
                  const Icon = iconMap[item.icon] || Zap;
                  return (
                    <div
                      key={idx}
                      draggable
                      onDragStart={(e) => onDragStart(e, item)}
                      style={{
                        padding: '10px 12px',
                        background: 'var(--bg-raised)',
                        borderRadius: 10,
                        border: '1px solid var(--border)',
                        cursor: 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                      className="palette-item"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                        <GripVertical size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            background: cat.bgColor,
                            color: cat.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Icon size={16} />
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: 'var(--text-primary)',
                              lineHeight: 1.2,
                            }}
                          >
                            {item.title}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: 'var(--text-secondary)',
                              lineHeight: 1.3,
                              marginTop: 2,
                            }}
                          >
                            {item.subtitle}
                          </div>
                        </div>
                      </div>

                      <button
                        className="btn btn-ghost btn-icon"
                        style={{
                          width: 26,
                          height: 26,
                          padding: 0,
                          borderRadius: 6,
                          background: 'var(--accent-soft)',
                          color: 'var(--accent)',
                          flexShrink: 0,
                        }}
                        title="Quick Add to Canvas"
                        onClick={() => handleQuickAdd(item)}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Info Banner */}
      <div
        style={{
          padding: '10px 16px',
          borderTop: '1px solid var(--border)',
          background: 'rgba(0,0,0,0.02)',
          fontSize: 10,
          color: 'var(--text-muted)',
          textAlign: 'center',
        }}
      >
        Tip: Drag block onto graph to position exactly
      </div>
    </aside>
  );
}
